'use client';

import LocaleSwitcher from '@/components/LocaleSwitcher';
import ShareButton from '@/components/ShareButton';
import HeaderAuthSection from '@/components/auth/HeaderAuthSection';
import { HamburgerMenu } from '@/components/layout/HamburgerMenu';
import { HAMBURGER_MENU_PAGES } from '@/components/layout/footerNavLinks';
import { ThemeSwitcher } from '@/components/theme/ThemeSwitcher';
import { useAuth } from '@/hooks/useAuth';
import { useRouteKind } from '@/hooks/useRouteKind';
import { Link } from '@/i18n/routing';
import Logo from '@/lib/logo/Logo';
import { Menu, SquarePen } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

export default function FloatingHeaderControls() {
  const t = useTranslations('Common');
  const { isAuthenticated: isSignedIn } = useAuth();
  const router = useRouter();
  // Route flags AND the canonical pathname derived from a single useRouteKind()
  // call — the single source of truth (#5878/#5895/#5901, #5879). Reading
  // `pathname` off the hook avoids a redundant second `usePathname()` call.
  // The previous inline checks compared `pathname` against un-interpolated Next.js
  // route-TEMPLATE strings (e.g. `/chat/[sessionId]`), which a real runtime path
  // (`/chat/abc-123`) never equals — so the flags were permanently false (dead).
  const {
    pathname,
    isSharePage,
    isChatSessionPage,
    isResearchSessionPage,
    isAnonSidebarPage,
  } = useRouteKind();
  // The mobile "New Chat" button is a session-only affordance: it appears on a
  // real chat OR research session (#5901/#5908), never on the empty landings or
  // share pages. Both navigate to `/chat` (the unified fresh-conversation entry,
  // matching AppSidebar/SessionItem) — a research session is the same
  // "start a fresh conversation" context.
  const showNewChatButton = isChatSessionPage || isResearchSessionPage;
  const isSearchPage =
    pathname.startsWith('/research') ||
    pathname.startsWith('/chat') ||
    pathname.startsWith('/search');
  const isDocumentPage = pathname.startsWith('/document');
  const isStoragePage = pathname.startsWith('/storage');
  const isDocGenPage =
    pathname === '/generovanie' || pathname.startsWith('/generovanie/');

  // Hide locale/theme switchers on pages with sidebars to avoid conflicts
  const hideFloatingSwitchers =
    isSearchPage || isDocumentPage || isStoragePage || isDocGenPage;
  // Hide Share on pages that manage their own header (doc-gen has Download/Copy)
  const hideShare = isDocGenPage;

  // Anonymous users on an anon-sidebar route (search/research/storage/
  // doc-analysis/chats-list — see useRouteKind's isAnonSidebarPage) get the
  // sidebar's own hamburger instead of the floating logo. Read the flag off
  // useRouteKind() rather than re-deriving the route set here: a second,
  // hand-maintained copy of this set is exactly what caused #7259 (this
  // component's copy silently excluded /chats while useRouteKind.ts's copy
  // did not, so the whole mobile menu unmounted the instant an anon user
  // followed the anon-visible "Chats" nav link).
  const hasAnonSidebar = !isSignedIn && isAnonSidebarPage;

  // Show hamburger menu only on specific pages
  const showHamburgerMenu = HAMBURGER_MENU_PAGES.some((page) =>
    pathname.startsWith(page)
  );

  return (
    <>
      {/* Floating controls (large screens only) */}
      <div
        className='fixed top-4 hidden compact-header:flex items-center gap-4'
        style={{
          zIndex: 1000,
          right:
            'calc(max(var(--reference-sidebar-width, 0px), var(--document-metadata-sidebar-width, 0px)) + 24px)',
          transition: 'right 240ms ease',
        }}
      >
        {!hideFloatingSwitchers && <ThemeSwitcher />}
        {!hideFloatingSwitchers && <LocaleSwitcher />}
        {!hideShare && <ShareButton />}
        <HeaderAuthSection />
        {/* Hamburger Menu - hidden on search/research pages */}
        {showHamburgerMenu && <HamburgerMenu />}
        {/* Show fixed logo for anonymous users, but not when they have sidebar */}
        {!isSignedIn && !hasAnonSidebar ? (
          <div className='fixed top-4 left-6 z-50'>
            <Link href='/' className='inline-flex min-h-[44px] items-center'>
              <Logo />
            </Link>
          </div>
        ) : null}
      </div>

      {/* Banner on small and medium screens */}
      <div
        className={
          'block compact-header:hidden fixed top-0 right-0 z-[1000] pointer-events-none left-0 mobile-view:left-[var(--sidebar-left-offset,0px)]'
        }
      >
        <div className='flex items-center justify-between px-4 py-3 bg-surface-subtle/95 dark:bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-surface-subtle/60 dark:supports-[backdrop-filter]:bg-background/60 pointer-events-auto'>
          {/* Left side - Menu button (when sidebar exists) or Logo */}
          <div className='flex-shrink-0'>
            {(isSignedIn && !isSharePage) || hasAnonSidebar ? (
              <div className='flex items-center mobile-view:hidden'>
                <button
                  type='button'
                  aria-label={t('openSidebar')}
                  title={t('openSidebar')}
                  className='flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md bg-transparent text-brand-action-muted transition-colors hover:bg-surface-subtle/60 hover:text-brand-tertiary'
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('sidebar:open'));
                  }}
                >
                  <Menu size={18} />
                </button>
              </div>
            ) : (
              <div className='flex items-center'>
                <Link
                  href='/'
                  className='inline-flex min-h-[44px] items-center'
                >
                  <Logo />
                </Link>
              </div>
            )}
          </div>

          {/* Right side - Share button and Menu */}
          <div className='flex items-center gap-2 sm:gap-4 flex-shrink-0'>
            {!hideFloatingSwitchers && <ThemeSwitcher />}
            {!hideFloatingSwitchers && <LocaleSwitcher />}
            {/* Share button - hidden on doc-gen (has its own Download/Copy) */}
            {!hideShare && (
              <>
                <div className='mobile-view:hidden'>
                  <ShareButton iconOnly={true} />
                </div>
                <div className='hidden mobile-view:block'>
                  <ShareButton />
                </div>
              </>
            )}
            {/* New Chat button - mobile only */}
            {showNewChatButton && (
              <button
                type='button'
                aria-label={t('newChat')}
                title={t('newChat')}
                className={
                  'mobile-view:hidden flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md bg-transparent text-brand-action-muted transition-colors hover:bg-surface-subtle/60 hover:text-brand-tertiary'
                }
                onClick={() => {
                  router.push('/chat');
                }}
              >
                <SquarePen size={18} />
              </button>
            )}
            <HeaderAuthSection />
            {/* Hamburger Menu - hidden on search/research pages */}
            {showHamburgerMenu && <HamburgerMenu />}
          </div>
        </div>
      </div>
    </>
  );
}
